import React, { useEffect, useState, useRef } from 'react';
import { Play, FileText, Clock, RefreshCw, Upload, FileUp, Users } from 'lucide-react';
import { IngestionStatus } from '../types';
import { api } from '../services/api';
import { useProject } from '../context/ProjectContext';
import { useNavigate } from 'react-router-dom';
import SpecReviewProgress from '../components/SpecReviewProgress';




const Ingestion: React.FC = () => {

  const { currentProject } = useProject();
  const navigate = useNavigate();
  const [status, setStatus] = useState<IngestionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTriggering, setIsTriggering] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [entitiesCount, setEntitiesCount] = useState(0);
  const logsEndRef = useRef<HTMLDivElement>(null);
  // pollingRef controls whether active polling is running (only during pipeline execution)
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stop any active polling loop
  const stopPolling = () => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Recursive polling: fetches status, reschedules if still running, stops when idle/error
  const pollUntilIdle = (projectId: string | undefined) => {
    pollingRef.current = setTimeout(async () => {
      try {
        const data = await api.getIngestionStatus(projectId);
        setStatus(data);
        if (data.current_status === 'idle' && projectId) {
          try {
            const entities = await api.getEntities(projectId);
            setEntitiesCount(entities.length);
          } catch { /* ignore */ }
        }
        // Keep polling only while actively running
        if (!['idle', 'error'].includes(data.current_status ?? 'idle')) {
          pollUntilIdle(projectId); // reschedule
        } else {
          pollingRef.current = null; // stops here
        }
      } catch {
        // On transient error, retry once more then stop
        pollingRef.current = null;
      }
    }, 10000);
  };

  // Load status once on mount / project change — no background interval
  useEffect(() => {
    const projectId = currentProject?.value;
    (async () => {
      try {
        const data = await api.getIngestionStatus(projectId);
        setStatus(data);
        setIsLoading(false);
        // If we open the page while a pipeline is already running, start polling
        if (!['idle', 'error'].includes(data.current_status ?? 'idle')) {
          pollUntilIdle(projectId);
        } else if (data.current_status === 'idle' && projectId) {
          const entities = await api.getEntities(projectId);
          setEntitiesCount(entities.length);
        }
      } catch {
        setIsLoading(false);
      }
    })();
    return () => stopPolling();
  }, [currentProject?.value]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [status?.logs]);

  const loadStatus = async () => {
    try {
      const data = await api.getIngestionStatus(currentProject?.value);
      setStatus(data);
      if (data.current_status === 'idle' && currentProject?.value) {
        try {
          const entities = await api.getEntities(currentProject.value);
          setEntitiesCount(entities.length);
        } catch (e) {
          console.error('Error loading entities count:', e);
          setEntitiesCount(0);
        }
      }
    } catch (error) {
      console.error('Failed to load status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrigger = async () => {
    if (!currentProject?.value) return;
    setIsTriggering(true);
    try {
      await api.triggerIngestion(currentProject.value);
      // Immediate status update then start recursive polling
      await loadStatus();
      stopPolling();
      pollUntilIdle(currentProject.value);
    } catch (error) {
      console.error('Failed to trigger:', error);
    } finally {
      setIsTriggering(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !currentProject?.value) return;

    try {
      setIsUploading(true);
      await api.uploadPdf(selectedFile, currentProject.value);
      setSelectedFile(null);
      // Reset input
      const fileInput = document.getElementById('pdf-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      await loadStatus();
    } catch (error) {
      console.error('Error uploading PDF:', error);
      alert('Error uploading PDF. Check logs.');
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Ingestion Pipeline</h2>
          <p className="text-slate-400">Monitor and control the data ingestion process from the Data Lake to the Test Case Generator.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center bg-slate-800 border border-slate-700 rounded-lg p-1">
            <input
              type="file"
              id="pdf-upload"
              accept=".pdf"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="pdf-upload"
              className="px-4 py-2 text-sm text-slate-300 hover:text-white cursor-pointer flex items-center gap-2"
            >
              <FileUp className="w-4 h-4" />
              {selectedFile ? selectedFile.name : 'Select PDF'}
            </label>
            {selectedFile && (
              <button
                onClick={handleUpload}
                disabled={isUploading || isTriggering}
                className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors transition-all"
              >
                {isUploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isUploading ? 'Uploading...' : 'Upload & Ingest'}
              </button>
            )}
          </div>
          <button
            onClick={loadStatus}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
          >
            <FileText className="w-4 h-4" />
            View Full Logs
          </button>
          {status?.current_status === 'idle' && entitiesCount > 0 && (
            <button
              onClick={() => navigate('/entities')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              <Users className="w-4 h-4" />
              Ver entidades detectadas ({entitiesCount})
            </button>
          )}
          <button
            onClick={handleTrigger}
            disabled={isTriggering || status?.current_status === 'running' || isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            <Play className="w-4 h-4" />
            {isTriggering ? 'Starting...' : 'Run Pipeline Now'}
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Current Status */}
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Current Status</p>
          <div className="flex items-center gap-3 mb-4">
            <span className={`w-3 h-3 rounded-full ${status?.current_status === 'running' ? 'bg-blue-400 animate-pulse' :
              status?.current_status === 'idle' ? 'bg-green-400' :
                'bg-red-400'
              }`}></span>
            <span className="text-2xl font-bold text-white capitalize">{status?.current_status || 'Unknown'}</span>
          </div>
          <p className="text-sm text-slate-400">System is healthy and waiting for next schedule.</p>
          <div className="mt-4 pt-4 border-t border-slate-700">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Health Check</span>
              <span className="text-green-400">{status?.health_percentage || 0}% uptime</span>
            </div>
            <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all"
                style={{ width: `${status?.health_percentage || 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Last Run */}
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Last Run</p>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status?.last_run?.status === 'completed' ? 'bg-green-500/10 text-green-400' :
              status?.last_run?.status === 'running' ? 'bg-blue-500/10 text-blue-400' :
                'bg-red-500/10 text-red-400'
              }`}>
              {status?.last_run?.status?.toUpperCase() || 'N/A'}
            </span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-slate-400" />
            <span className="text-xl font-bold text-white">{formatDate(status?.last_run?.started_at)}</span>
          </div>
          <p className="text-sm text-slate-400">
            Duration: {status?.last_run?.duration_seconds ? `${Math.round(status.last_run.duration_seconds / 60)}m ${status.last_run.duration_seconds % 60}s` : 'N/A'} • {status?.last_run?.chunks_indexed || 0} records
          </p>
        </div>

        {/* Next Scheduled Run */}
        <div className="bg-surface-dark border border-slate-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Next Scheduled Run</p>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400">
              SCHEDULED
            </span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <RefreshCw className="w-5 h-5 text-slate-400" />
            <span className="text-xl font-bold text-white">{formatDate(status?.next_scheduled_run)}</span>
          </div>
          <p className="text-sm text-slate-400">Approx. 30 mins interval</p>
        </div>
      </div>

      {/* Spec Review Progress - Show after ingestion completes */}
      {currentProject?.value && status?.current_status === 'idle' && (
        <SpecReviewProgress projectId={currentProject.value} />
      )}

      {/* Logs Section (Full Width) */}
      <div className="bg-surface-dark border border-slate-800 rounded-xl overflow-hidden h-[500px] flex flex-col">
        <div className="px-6 py-4 bg-slate-800/50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <h3 className="font-semibold text-white">Live System Log</h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
            <span className="w-2 h-2 bg-red-400 rounded-full"></span>
          </div>
        </div>
        <div className="p-4 bg-slate-900 font-mono text-sm flex-1 overflow-y-auto">
          {status?.logs.map((log, index) => (
            <div key={index} className={`py-1 ${log.includes('ERROR') || log.includes('❌') ? 'text-red-400' :
              log.includes('INFO') || log.includes('✅') ? 'text-green-400' :
                log.includes('Waiting') ? 'text-yellow-400 animate-pulse' :
                  'text-slate-400'
              }`}>
              {log}
            </div>
          ))}
          {(!status?.logs || status.logs.length === 0) && (
            <div className="text-slate-500 py-4 text-center">No logs available</div>
          )}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>

  );
};

export default Ingestion;
